<template>
  <v-content>
    <v-container>
      <list
        ref="list"
        :title="title"
        :headers="headers"
        :data="data"
        :response="response"
        :loading="loading"
        @edit="editFn"
        @delete="deleteFn"
        @details="previewFn"
        @new="newFn"
        @reload="loadBoats()"
        @sort="loadBoats()"
        @hook:mounted="handleMount('list')"
      >
        <template v-slot:boat-import v-if="canUseImport">
          <v-btn text @click="openBoatImportDialog" color="primary">{{
            $t("builder.boats.list.importBoat")
          }}</v-btn>
        </template>

        <template v-slot:select-headers>
          <filter-menu
            ref="filterMenu"
            :headers="headerOptions"
            :filters="filterOptions"
            :menu="filterMenu"
            :show-filter-options="true"
            location="boats"
            @close="handleCloseFilterMenu"
            @update:headers="handleUpdateHeaders"
            @update:filters="handleUpdateFilters"
            @hook:mounted="handleMount('filterMenu')"
          ></filter-menu>
        </template>
      </list>
      <delete-dialog
        :dialog="deleteDialog"
        :item="boatName"
        @agree="remove"
        v-show="false"
      />
      <import-boat
        :dialog="importDialog"
        @import="handleBoatImport"
        v-if="importDialog.active"
      />
    </v-container>
  </v-content>
</template>

<script>
import api from "@/utils/api.js";
import List from "@/components/lists/BuilderList.vue";
import DeleteDialog from "@/components/dialogs/DeleteDialog.vue";
import ImportBoat from "@/components/dialogs/ImportBoatDialog.vue";
import snackbar from "@/utils/snackbar.js";
import { mapGetters } from "vuex";
import FilterMenu from "@/components/general/FilterMenu.vue";
import restrictions from "@/utils/restrictions";

export default {
  components: {
    List,
    DeleteDialog,
    ImportBoat,
    FilterMenu,
  },
  data: () => ({
    mounted: {
      filterMenu: { mounted: false, refs: "filterMenu" },
      list: { mounted: false, refs: "list" },
      root: { mounted: false, refs: null },
    },
    filterMenu: false,
    filterOptions: {
      title: "",
      items: [{ title: "", options: [{ text: "", value: "" }] }],
    },
    headerOptions: [],
    headers: [],
    data: [],
    sort: "name",
    title: "Boats",
    deleteDialog: false,
    boat: null,
    boatName: null,
    loading: true,
    response: null,
    importDialog: { active: false },
    abortController: null,
    abortSignal: null,
  }),
  created() {
    this.setInitialData();
  },

  mounted() {
    //NOTE: this is here to prevent multiple API calls to the same function. To gether all the parameters from the different components.
    this.handleMount("root");
  },

  computed: {
    ...mapGetters(["getMe"]),
    canUseImport() {
      return this.getMe.features.can_import_from_mis;
    },
  },
  methods: {
    handleMount(componentName) {
      if (!this.mounted.hasOwnProperty(componentName)) {
        console.error("BOATS:component name does not exists", componentName);
        return;
      }

      this.mounted[componentName] = {
        ...this.mounted[componentName],
        mounted: true,
      };

      if (Object.values(this.mounted).every((item) => item.mounted)) {
        this.loadBoats();
      }
    },
    setInitialData() {
      this.title = this.$t("builder.boats.list.boats");

      this.filterOptions = {
        title: this.$t("builder.boats.list.filterBoatsBy"),
        items: [
          {
            title: this.$t("builder.boats.list.source"),
            is_hidden: !restrictions.canUseCrmFeature(
              "can_oem_manage_non_group_boats"
            ),
            options: [
              {
                text: this.$t("builder.boats.list.all"),
                value: "source_all",
                active: true,
                default: true,
                server: { name: "is_external_brand", value: null },
              },
              {
                text: this.$t("builder.boats.list.organizationBoats"),
                value: "source_organization_boats_only",
                active: false,
                default: false,
                server: { name: "is_external_brand", value: false },
              },
              {
                text: this.$t("builder.boats.list.nonOrganizationBoats"),
                value: "source_non_organization_boats_only",
                active: false,
                default: false,
                server: { name: "is_external_brand", value: true },
              },
            ],
          },
          {
            title: this.$t("builder.boats.list.productionState"),
            is_hidden: false,
            options: [
              {
                text: this.$t("builder.boats.list.all"),
                value: "production_state_all",
                active: true,
                default: true,
                server: { name: "is_in_production", value: null },
              },

              {
                text: this.$t("builder.boats.list.inProduction"),
                value: "production_state_in_production",
                active: false,
                default: false,
                server: { name: "is_in_production", value: true },
              },
              {
                text: this.$t("builder.boats.list.notInProduction"),
                value: "production_state_not_in_production",
                active: false,
                default: false,
                server: { name: "is_in_production", value: false },
              },
            ],
          },
          {
            title: this.$t("builder.boats.list.connectivity"),
            is_hidden: false,
            options: [
              {
                text: this.$t("builder.boats.list.all"),
                value: "connectivity_all",
                active: true,
                default: true,
                server: { name: "is_connected", value: null },
              },

              {
                text: this.$t("builder.boats.list.connectedBoats"),
                value: "connectivity_connected_boats",
                active: false,
                default: false,
                server: { name: "is_connected", value: true },
              },
              {
                text: this.$t("builder.boats.list.liteNonConnected"),
                value: "connectivity_non_connected_boats",
                active: false,
                default: false,
                server: { name: "is_connected", value: false },
              },
            ],
          },
          {
            title: this.$t("builder.boats.list.appAccess"),
            is_hidden: false,
            options: [
              {
                text: this.$t("builder.boats.list.all"),
                value: "pending_app_access_all",
                active: true,
                default: true,
                server: { name: "is_pending_validation", value: null },
              },
              {
                text: this.$t("builder.boats.list.reviewPending"),
                value: "pending_app_access_review_pending",
                active: false,
                default: false,
                server: { name: "is_pending_validation", value: true },
              },
            ],
          },
        ],
      };
      this.headerOptions = [
        {
          text: this.$t("builder.boats.list.brand"),
          align: "left",
          sortable: true,
          orderingName: "model__brand_name",
          value: "model.brand_name",
          canHide: true,
        },
        {
          text: this.$t("builder.boats.list.model"),
          align: "left",
          sortable: true,
          orderingName: "model__model",
          value: "model.model",
          canHide: true,
        },
        {
          text: this.$t("builder.boats.list.boatName"),
          align: "right",
          value: "boat_name",
          orderingName: "boat_name",
          sortable: false,
          canHide: true,
        },
        {
          text: this.$t("builder.boats.list.slotNumber"),
          align: "left",
          sortable: true,
          orderingName: "slot_number",
          value: "slot_number",
          canHide: true,
        },
        {
          text: this.$t("builder.boats.list.HIN"),
          align: "left",
          sortable: true,
          orderingName: "ce_number",
          value: "ce_number",
          canHide: true,
        },
        {
          text: this.$t("builder.boats.list.sentinelSN"),
          align: "left",
          sortable: true,
          orderingName: "device__name",
          value: "device_serial",
          canHide: true,
        },
        {
          text: this.$t("builder.boats.list.servicingPartner"),
          align: "left",
          sortable: true,
          orderingName: "servicing_partner_name",
          value: "servicing_partner",
          canHide: true,
        },
        {
          text: this.$t("builder.boats.list.ownershipStatus"),
          align: "left",
          sortable: false,
          orderingName: "ownership_status",
          value: "ownership_status",
          canHide: true,
        },
        {
          text: this.$t("builder.boats.list.subscriptionStatus"),
          align: "left",
          sortable: false,
          orderingName: "subscription",
          value: "billing_info",
          canHide: true,
        },
        {
          text: "",
          align: "right",
          value: "action",
          orderingName: "model__brand_name",
          sortable: false,
          canHide: false,
        },
      ];
    },

    copyToClipboard(item) {
      navigator.clipboard
        .writeText(item.ce_number)
        .then((resp) => {
          snackbar.success(
            this.$t("utils.snackbar.success.copyingToClipboardWasSuccessful")
          );
        })
        .catch((err) => {
          snackbar.error(
            this.$t("utils.snackbar.error.copyingToClipboardWasNotSuccessful")
          );
        });
    },

    handleCloseFilterMenu(value) {
      this.filterMenu = value;
    },

    handleUpdateHeaders(headers, menu = true) {
      this.headers = this.headerOptions.filter((item) => {
        return headers.includes(item.value) || !item.canHide;
      });
      this.filterMenu = menu;
    },

    openBoatImportDialog() {
      this.importDialog.active = true;
    },
    handleBoatImport(event) {
      if (!event) {
        this.importDialog.active = false;
        return;
      }

      api
        .importBoatFromMis({ device_serial: event })
        .then((resp) => {
          this.$router.push({
            name: "boatsEdit",
            params: { id: resp.data.uuid },
          });
        })
        .catch((err) => {
          snackbar.error(
            err.response.data[Object.keys(err.response.data)[0]][0]
          );
          this.importDialog.active = false;
        });
    },
    handleUpdateFilters() {
      if (this.$refs.list == null) return;
      this.$refs.list.setPage(1);
      this.loadBoats();
    },
    loadBoats() {
      if (this.abortController == null) {
        this.abortController = new AbortController();
        this.abortSignal = this.abortController.signal;
      } else {
        this.abortController.abort();
        this.abortController = new AbortController();
        this.abortSignal = this.abortController.signal;
      }

      let params = {};

      for (const key in this.mounted) {
        const refs = this.mounted[key].refs;
        if (refs == null) continue;
        params = {
          ...params,
          ...this.$refs[refs].query,
        };
      }
      this.$router.replace({
        name: this.$route.name,
        query: params,
      });

      this.loading = true;
      this.data = [];
      api
        .getBoatList(params, this.abortSignal)
        .then((resp) => {
          this.response = resp.data;
          this.data = resp.data.results;
          this.loading = false;
          this.abortController = null;
          this.abortSignal = null;
        })
        .catch((err) => {
          this.loading = false;
          this.abortController = null;
          this.abortSignal = null;
        });
    },
    editFn(boat) {
      this.$router.push({
        name: "boatsEdit",
        params: { id: boat.uuid },
      });
    },
    deleteFn(boat) {
      this.deleteDialog = true;
      this.boat = boat;
      this.boatName = boat.model.brand_name + " " + boat.model.model;
    },

    remove(agreed) {
      if (agreed) {
        api.deleteBoat(this.boat.uuid).then((resp) => {
          snackbar.success(
            this.$t("utils.snackbar.success.successfullyDeleted")
          );
          this.loadBoats();
        });
      }
      this.deleteDialog = false;
    },

    previewFn(boat) {
      this.$router.push({
        name: "boatsDetails",
        params: { id: boat.uuid },
      });
    },
    newFn(item) {
      this.$router.push({
        name: "boatsNew",
      });
    },
  },
};
</script>
